import * as React from "react";
import { useRef, useState } from "react";
import apiClient from "@/api-client";
import { Box, Chip, CircularProgress, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import type { Option } from "@/components/customTable/types";
import { CaretDown } from "@phosphor-icons/react";

export function FilterFetchMultiSelect({
	label,
	errorText,
	showInputLabel,
	value,
	required,
	placeholder,
	onChange,
	disabled,
	getOptionsUrl,
	fetchLabel,
	fetchLabel2,
	fetchValue,
	disabledOptions = [],
	disabledConditionKey,
	notDisabledConditionKey,
	notDisabledOptions = [],
	indicatorKey,
	indicatorTooltip,
}: {
	label?: string;
	errorText?: string;
	showInputLabel?: boolean;
	value: Option[];
	required?: boolean;
	placeholder: string;
	onChange: (value: Option[]) => void;
	disabled?: boolean;
	getOptionsUrl: string;
	fetchLabel: string;
	fetchLabel2?: string;
	fetchValue: string;
	disabledOptions?: string[];
	disabledConditionKey?: string;
	notDisabledConditionKey?: string;
	notDisabledOptions?: string[];
	indicatorKey?: string;
	indicatorTooltip?: string;
}) {
	const hasIndicator = Boolean(indicatorKey && indicatorTooltip);
	const [options, setOptions] = useState<any[]>([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [page, setPage] = useState(1);
	const [isOpen, setIsOpen] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

	const fetchOptions = async (searchTerm: string, pageNum: number, append = false) => {
		try {
			setLoading(true);
			setIsMounted(true);
			setPage(pageNum);
			const limit = 10;
			const offset = (pageNum - 1) * limit;
			const url = getOptionsUrl.includes("?")
				? `${getOptionsUrl}&search=${searchTerm}&limit=${limit}&offset=${offset}`
				: `${getOptionsUrl}?search=${searchTerm}&limit=${limit}&offset=${offset}`;

			const res = await apiClient.get(url);
			const newOptions = res.data.data.map((option: any) => ({
				value: option[fetchValue] || option.value,
				label: fetchLabel2 ? `${option[fetchLabel]} ${option[fetchLabel2]}` : option[fetchLabel] || option.label,
				...(hasIndicator ? { indicator: Boolean(option[indicatorKey!]) } : {}),
				disabled: disabledConditionKey
					? notDisabledOptions.includes(option[fetchValue])
						? false
						: option[disabledConditionKey] || (notDisabledConditionKey && !option[notDisabledConditionKey])
					: false,
			}));

			if (append) {
				setOptions((prev) => [...prev, ...newOptions]);
			} else {
				setOptions(newOptions);
			}

			setHasMore(newOptions.length > 0);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (event: any, newValue: string, reason: string) => {
		if (reason !== "selectOption" && reason !== "reset" && reason !== "blur") {
			setSearch(newValue);

			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			setOptions([]);
			setLoading(true);

			debounceTimeout.current = setTimeout(() => {
				fetchOptions(newValue, 1, false);
			}, 300);
		}
	};

	const handleChange = (event: any, newValue: any) => {
		if (newValue) {
			onChange(
				newValue.map((option: any) => ({
					value: option.value,
					label: option.label,
					...(hasIndicator ? { indicator: Boolean(option.indicator) } : {}),
				}))
			);
		} else {
			onChange([]);
		}
	};

	const handleOpen = () => {
		setIsOpen(true);

		if (!isMounted) {
			fetchOptions("", 1, false);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	return (
		<FormControl fullWidth key={label} error={!!errorText}>
			<Autocomplete
				options={options}
				multiple
        popupIcon={<CaretDown size={18} />}   
				getOptionLabel={(option: any) => option.label}
				getOptionDisabled={(option: any) => disabledOptions?.includes(option.value) || !!option.disabled || false}
				isOptionEqualToValue={(option, optionValue) => String(option.value) === String(optionValue.value)}
				value={value}
				disableCloseOnSelect
				onChange={handleChange}
				onInputChange={handleInputChange}
				onOpen={handleOpen}
				forcePopupIcon
				onClose={handleClose}
				inputValue={isOpen ? search : ""}
				disabled={disabled}
				loading={loading}
				filterOptions={(x) => x}
        sx={{
          '.MuiInputBase-root': {
            minHeight: showInputLabel ? 'auto !important' : '44px !important',
            py: '6px !important',
            pl: value.length > 0 ? '6px !important' : '12px !important',
          },
        }}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={isOpen ? "Search..." : placeholder}
						label={showInputLabel ? label : ""}
						error={!!errorText}
						required={required}
					/>
				)}
				ListboxProps={{
          role: 'list-box',
					style: { maxHeight: 220, fontSize: "16px" },
					onScroll: (event: React.UIEvent<HTMLUListElement>) => {
						const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
						const isNearBottom = scrollTop + clientHeight >= scrollHeight - 40;

						if (isNearBottom && hasMore && !loading) {
							fetchOptions(search, page + 1, true);
						}
					},
				}}
				slotProps={{
					chip: { color: "primary", size: "small", sx: { mr: "8px !important" } },
					popper: {
            sx: {
							"& .MuiAutocomplete-option": {
								margin: "4px 0 !important",
								fontSize: "16px !important",
							},
						},
					},
					paper: {
						sx: {
							fontSize: "16px !important",
						},
					},
				}}
				{...(hasIndicator
					? {
							renderTags: (tagValue: Option[], getTagProps: (params: { index: number }) => any) =>
								tagValue.map((option, index) => {
									const { key, ...tagProps } = getTagProps({ index });
									return (
										<Chip
											key={key}
											label={
												<>
													{option.label}
													{option.indicator && ` (${indicatorTooltip!})`}
												</>
											}
											color="primary"
											size="small"
											sx={{ mr: "8px !important" }}
											{...tagProps}
										/>
									);
								}),
						}
					: {})}
				renderOption={(props, option, state) => {
					const isLast = state.index === options.length - 1;

					return (
						<Box
							component="li"
							{...props}
							key={String(option.value)}
							sx={{
								mb: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
								...(isLast && { flexDirection: "column", alignItems: "flex-start !important", mb: 0 }),
							}}
						>
							<Box
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								width="100%"
								height="100%"
								flex={1}
							>
								<span style={{ flexGrow: 1, flexShrink: 1, minWidth: "60%" }}>{option.label}</span>
								{hasIndicator && option.indicator ? (
									<Tooltip title={indicatorTooltip!} arrow>
										<Chip label={indicatorTooltip!} size="small" variant="outlined" color="default" sx={{ ml: 1, flexShrink: 0 }} />
									</Tooltip>
								) : null}
							</Box>
							{isLast && loading && hasMore && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										padding: 1,
										width: "100%",
										marginTop: 1,
									}}
								>
									<CircularProgress size={20} />
								</Box>
							)}
						</Box>
					);
				}}
			/>
		</FormControl>
	);
}
